import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { Json } from "@how/database";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

/**
 * An audience is a saved definition, re-resolved every time it is used (communications plan task 3, ADR 0035). The
 * filter shapes below are the migration's contract (`20260924290000_audiences.sql`); every filter must match, and the
 * ids or values inside one filter are alternatives.
 */
export type AudienceChannel = "sms" | "email";

export type MembershipStanding = "visitor" | "attending" | "in_process" | "member" | "inactive";
export const STANDING_LABEL: Record<MembershipStanding, string> = {
  visitor: "Visitor",
  attending: "Attending",
  in_process: "In process",
  member: "Member",
  inactive: "Inactive",
};

export type CustomFieldOp =
  "equals" | "contains" | "before" | "after" | "is_true" | "is_false" | "any_of";

export type AudienceFilter =
  | { readonly type: "tag"; readonly tag_ids: readonly string[] }
  | { readonly type: "campus"; readonly campus_ids: readonly string[] }
  | { readonly type: "group"; readonly group_ids: readonly string[] }
  | { readonly type: "standing"; readonly values: readonly MembershipStanding[] }
  | { readonly type: "household_role"; readonly values: readonly ("adult" | "child")[] }
  | {
      readonly type: "custom_field";
      readonly definition_id: string;
      readonly op: CustomFieldOp;
      readonly value?: string | readonly string[];
    };

export interface AudienceDefinition {
  readonly filters: readonly AudienceFilter[];
}

export interface AudienceRow {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly definition: AudienceDefinition;
  readonly updatedAt: string;
}

/** The counts the editor shows before anything is sent, kept apart on purpose. */
export interface AudienceCounts {
  readonly matched: number;
  readonly children: number;
  readonly reachable: number;
  readonly notAgreed: number;
  readonly noAddress: number;
}

const asDefinition = (value: unknown): AudienceDefinition => {
  const filters =
    typeof value === "object" &&
    value !== null &&
    Array.isArray((value as { filters?: unknown }).filters)
      ? (value as { filters: AudienceFilter[] }).filters
      : [];
  return { filters };
};

/** The church's saved audiences. A church has tens, so the list is read whole. */
export function useAudiences() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["audiences", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<AudienceRow[]> => {
      const { data, error } = await supabase
        .from("message_audiences")
        .select("id, name, description, definition, updated_at")
        .eq("tenant_id", tenantId ?? "")
        .is("deleted_at", null)
        .order("name");
      if (error) throw error;
      return data.map((row) => ({
        id: row.id,
        name: row.name,
        description: row.description,
        definition: asDefinition(row.definition),
        updatedAt: row.updated_at,
      }));
    },
  });
}

/**
 * The counts for a definition as it is being edited, on a channel. Keyed by the definition itself, so a change to a
 * filter is a new query and an unchanged one is served from the cache.
 */
export function useAudienceCounts(definition: AudienceDefinition, channel: AudienceChannel) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["audience-counts", tenantId, channel, definition],
    enabled: tenantId !== null,
    placeholderData: (previous) => previous,
    queryFn: async (): Promise<AudienceCounts> => {
      const { data, error } = await supabase.rpc("audience_counts", {
        p_tenant_id: tenantId ?? "",
        p_definition: definition as unknown as Json,
        p_channel: channel,
      });
      if (error) throw error;
      const counts = data as unknown as Partial<Record<keyof AudienceCounts, number>>;
      return {
        matched: counts.matched ?? 0,
        children: counts.children ?? 0,
        reachable: counts.reachable ?? 0,
        notAgreed: counts.notAgreed ?? 0,
        noAddress: counts.noAddress ?? 0,
      };
    },
  });
}

const failed = (error: { message: string; code?: string } | null) => {
  if (error !== null) throw Object.assign(new Error(error.message), { code: error.code });
};

export function useSaveAudience() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      readonly audienceId: string | null;
      readonly name: string;
      readonly description: string;
      readonly definition: AudienceDefinition;
    }): Promise<string> => {
      const { data, error } = await supabase.rpc("set_audience", {
        p_tenant_id: tenantId ?? "",
        p_name: input.name,
        ...(input.audienceId === null ? {} : { p_audience_id: input.audienceId }),
        p_description: input.description,
        p_definition: input.definition as unknown as Json,
      });
      failed(error);
      return data as unknown as string;
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["audiences", tenantId] });
    },
  });
}

export function useDeleteAudience() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (audienceId: string) => {
      const { error } = await supabase.rpc("delete_audience", { p_audience_id: audienceId });
      failed(error);
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["audiences", tenantId] });
    },
  });
}

/** The church's custom field definitions, for the custom field filter. */
export function useCustomFieldDefinitions() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["custom-field-definitions", tenantId],
    enabled: tenantId !== null,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("custom_field_definitions")
        .select("id, key, label, field_type, options")
        .eq("tenant_id", tenantId ?? "")
        .order("sort_order")
        .order("label");
      if (error) throw error;
      return data;
    },
  });
}
